/**
 * [TD-47.2b] SnakeActionDrawer - snake's pick initiation surface.
 *
 * Renders inline below the clicked player row in PlayerPoolWithDrawer.
 * Single-click commit (Gap 2 verdict B): clicking DRAFT immediately fires
 * the snake REST `/api/draft/{leagueId}/submit-pick` via the parent's
 * onDraft handler - no confirmation step.
 *
 * Mirrors `AuctionActionDrawer.jsx` shape; differs in:
 *   • No opening-bid input (snake picks have no price)
 *   • DRAFT button copy + green tone (deliberate "this is the pick" cue)
 *   • Disabled state surfaces server-side errors (e.g. duplicate request_id
 *     resubmission collisions) with a small inline message
 *
 * Props:
 *   player    - { id, name, position, ... } the row clicked
 *   onDraft   - async () → Promise<void>; parent wires REST call + busy state
 *   onCancel  - () → void; closes the drawer without committing
 *   busy      - boolean; disables DRAFT during in-flight submit
 *   error     - string|null; surfaced inline on rejection
 *   proxyTeamName - [Item 3] set when the COMMISSIONER is picking for the
 *               manager on the clock ("Draft For LM"); flips the copy so it
 *               is impossible to confuse with picking for yourself
 */
import PositionBadge from "../PositionBadge";

export default function SnakeActionDrawer({
  player,
  onDraft,
  onCancel,
  busy = false,
  error = null,
  proxyTeamName = null,
}) {
  if (!player) return null;

  const handleKey = (e) => {
    if (e.key === "Enter" && !busy) {
      e.preventDefault();
      onDraft?.();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel?.();
    }
  };

  return (
    <div
      className="border-y border-[#FF6B00]/35 px-3 py-2"
      style={{ background: "rgba(255,107,0,0.08)" }}
      data-testid="snake-action-drawer"
      onKeyDown={handleKey}
    >
      <div className="flex items-center gap-2 mb-2">
        <PositionBadge position={player.position} size="sm" />
        <p className="font-body text-xs text-[#F5F5F5] leading-tight flex-1 min-w-0">
          {proxyTeamName ? (
            <>
              Drafting <span className="text-[#FF6B00] font-semibold">{player.name}</span>
              {" for "}
              <span className="text-[#FF6B00] font-semibold">{proxyTeamName}</span>
            </>
          ) : (
            <>Drafting <span className="text-[#FF6B00] font-semibold">{player.name}</span></>
          )}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onDraft}
          disabled={busy}
          autoFocus
          data-testid={proxyTeamName ? "snake-action-drawer-draft-for-lm-btn" : "snake-action-drawer-draft-btn"}
          className="flex-1 bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-50 disabled:cursor-not-allowed text-white font-display font-black text-[12px] uppercase tracking-wider px-3 py-2 rounded transition-all active:scale-95"
        >
          {busy
            ? "Drafting..."
            : proxyTeamName
              ? `Draft For ${proxyTeamName}`
              : `Draft ${player.name}`}
        </button>
        <button
          onClick={onCancel}
          disabled={busy}
          data-testid="snake-action-drawer-cancel-btn"
          className="text-[rgba(255,255,255,0.50)] hover:text-[#F5F5F5] font-body text-xs px-2 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
      {error && (
        <p
          className="mt-1.5 text-[10px] text-[#FF4444] font-body"
          data-testid="snake-action-drawer-error"
        >
          {error}
        </p>
      )}
    </div>
  );
}
